import { Link } from 'react-router-dom'
import { CheckSquare, AlarmClock, Moon, User, ArrowRight } from 'lucide-react'
import { useDormantProjects } from '../../hooks/useDormantProjects.js'

function TaskRow({ project, overdue }) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-white">{project.title || project.projectId}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-white/40">
          {project.customerName && (
            <span className="flex items-center gap-1"><User className="h-3 w-3" />{project.customerName}</span>
          )}
          {project.commercialStatus && <span>{project.commercialStatus}</span>}
        </div>
      </div>
      {overdue ? (
        <span className="shrink-0 rounded-full border border-red-500/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-semibold text-red-400">
          en retard
        </span>
      ) : (
        <span className="shrink-0 rounded-full border border-[#5b5cf4]/30 bg-[#5b5cf4]/10 px-2 py-0.5 text-[10px] font-semibold text-[#5b5cf4]">
          aujourd'hui
        </span>
      )}
    </li>
  )
}

export default function DailyActions() {
  const { data, isLoading } = useDormantProjects()
  const d = data?.data

  const today = (d?.autoProjects || []).filter((p) => p.reason === 'relance_today')
  const overdue = (d?.autoProjects || []).filter((p) => p.reason === 'overdue_relance')
  const dormantCount = d?.counts?.dormant ?? 0
  const nothing = !isLoading && today.length === 0 && overdue.length === 0

  return (
    <section className="mb-6 rounded-2xl border border-white/10 bg-black/20 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-white/80">
          <CheckSquare className="h-4 w-4 text-[#5b5cf4]" />
          Mes actions du jour
        </h2>
        <span className="text-xs text-white/35">
          {new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date())}
        </span>
      </div>

      {isLoading ? (
        <p className="py-6 text-center text-sm text-white/30">Chargement…</p>
      ) : nothing ? (
        <p className="py-6 text-center text-sm text-white/40">Rien de prévu aujourd'hui. 🎉</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-[#5b5cf4]/80">
              <AlarmClock className="h-3.5 w-3.5" /> À relancer aujourd'hui ({today.length})
            </p>
            {today.length === 0 ? (
              <p className="rounded-xl border border-white/5 py-4 text-center text-xs text-white/25">Aucune relance prévue</p>
            ) : (
              <ul className="space-y-2">
                {today.map((p) => <TaskRow key={p.projectId} project={p} />)}
              </ul>
            )}
          </div>
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-red-400/80">
              <AlarmClock className="h-3.5 w-3.5" /> En retard ({overdue.length})
            </p>
            {overdue.length === 0 ? (
              <p className="rounded-xl border border-white/5 py-4 text-center text-xs text-white/25">Rien en retard 👍</p>
            ) : (
              <ul className="space-y-2">
                {overdue.map((p) => <TaskRow key={p.projectId} project={p} overdue />)}
              </ul>
            )}
          </div>
        </div>
      )}

      {dormantCount > 0 && (
        <Link
          to="/dashboard/crm/relances"
          className="mt-3 flex items-center gap-1.5 text-xs text-orange-400/80 transition hover:text-orange-400"
        >
          <Moon className="h-3.5 w-3.5" />
          {dormantCount} projet{dormantCount > 1 ? 's' : ''} dormant{dormantCount > 1 ? 's' : ''} à réveiller
          <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </section>
  )
}
